import React, { useRef, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheckCircle, faPlayCircle } from '@fortawesome/free-solid-svg-icons';
import './TrainingVideoPlayer.css';

const TrainingVideoPlayer = ({ video, videos = [], progress, onClose, onProgressUpdate, onNextVideo }) => {
    const videoRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showNextVideo, setShowNextVideo] = useState(false);
    const [showRewatchPrompt, setShowRewatchPrompt] = useState(false);
    const [countdown, setCountdown] = useState(5);
    const progressUpdateInterval = useRef(null);
    const countdownInterval = useRef(null);
    
    // Find next video in the list
    const currentIndex = videos.findIndex(v => v.id === video.id);
    const nextVideo = currentIndex !== -1 && currentIndex < videos.length - 1 
        ? videos[currentIndex + 1] 
        : null;
    
    // Check if video was already completed
    const isAlreadyCompleted = progress?.is_completed || false;
    
    useEffect(() => {
        // Reset next video overlay when video changes
        setShowNextVideo(false);
        setCountdown(5);
        
        // Clear any existing countdown
        if (countdownInterval.current) {
            clearInterval(countdownInterval.current);
            countdownInterval.current = null;
        }
        
        // Check if video is already completed and show rewatch prompt
        if (isAlreadyCompleted && nextVideo) {
            console.log('Video already completed, showing rewatch prompt');
            setShowRewatchPrompt(true);
        } else {
            // Set initial playback position from saved progress
            if (videoRef.current && progress?.last_watched_position && !isAlreadyCompleted) {
                videoRef.current.currentTime = progress.last_watched_position;
            }
            
            // Auto-play only if not showing rewatch prompt
            if (videoRef.current && !showRewatchPrompt) {
                videoRef.current.play().catch(error => {
                    console.log('Auto-play prevented:', error);
                });
            }
        }
        
        // Cleanup on unmount
        return () => {
            if (progressUpdateInterval.current) {
                clearInterval(progressUpdateInterval.current);
                progressUpdateInterval.current = null;
            }
            if (countdownInterval.current) {
                clearInterval(countdownInterval.current);
                countdownInterval.current = null;
            }
            handleProgressSave();
        };
    }, [video.id]);
    
    // Update progress every 5 seconds while playing
    useEffect(() => {
        if (isPlaying) {
            progressUpdateInterval.current = setInterval(() => {
                handleProgressSave();
            }, 5000);
        } else {
            if (progressUpdateInterval.current) {
                clearInterval(progressUpdateInterval.current);
            }
        }
        
        return () => {
            if (progressUpdateInterval.current) {
                clearInterval(progressUpdateInterval.current);
            }
        };
    }, [isPlaying, currentTime]);
    
    const handleProgressSave = () => {
        if (videoRef.current && duration > 0) {
            const completionPercentage = Math.round(
                (videoRef.current.currentTime / duration) * 100
            );
            
            const progressData = {
                last_watched_position: Math.round(videoRef.current.currentTime),
                completion_percentage: completionPercentage,
                is_completed: completionPercentage >= 95
            };
            
            onProgressUpdate(video.id, progressData);
        }
    };
    
    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);
        }
    };
    
    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            setDuration(videoRef.current.duration);
        }
    };
    
    const handlePlay = () => {
        setIsPlaying(true);
    };
    
    const handlePause = () => {
        setIsPlaying(false);
        handleProgressSave();
    };
    
    const handleEnded = () => {
        console.log('Video ended. Next video available:', !!nextVideo);
        setIsPlaying(false);
        
        // Mark as completed
        onProgressUpdate(video.id, {
            last_watched_position: Math.round(duration),
            completion_percentage: 100,
            is_completed: true
        });
        
        // Show next video overlay if there's a next video
        if (nextVideo && onNextVideo) {
            console.log('Showing next video overlay for:', nextVideo.title);
            setShowNextVideo(true);
            startCountdown();
        } else {
            console.log('No next video available or onNextVideo not provided');
        }
    };
    
    const startCountdown = () => {
        setCountdown(5);
        let timeLeft = 5;
        
        countdownInterval.current = setInterval(() => {
            timeLeft -= 1;
            
            if (timeLeft <= 0) {
                clearInterval(countdownInterval.current);
                // Auto-play next video
                if (onNextVideo) {
                    console.log('Auto-playing next video after countdown');
                    onNextVideo(video.id);
                }
            } else {
                setCountdown(timeLeft);
            }
        }, 1000);
    };
    
    const cancelNextVideo = () => {
        console.log('Cancelled auto-play');
        setShowNextVideo(false);
        setCountdown(5);
        if (countdownInterval.current) {
            clearInterval(countdownInterval.current);
            countdownInterval.current = null;
        }
    };
    
    const playNextNow = () => {
        console.log('Playing next video immediately');
        if (countdownInterval.current) {
            clearInterval(countdownInterval.current);
            countdownInterval.current = null;
        }
        if (onNextVideo) {
            onNextVideo(video.id);
        }
    };
    
    const handleRewatch = () => {
        console.log('User chose to rewatch');
        setShowRewatchPrompt(false);
        // Reset video to beginning
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play().catch(error => {
                console.log('Auto-play prevented:', error);
            });
        }
    };
    
    const handleSkipToNext = () => {
        console.log('User chose to skip to next video');
        setShowRewatchPrompt(false);
        if (onNextVideo) {
            onNextVideo(video.id);
        }
    };
    
    const handleClose = () => {
        handleProgressSave();
        onClose();
    };
    
    return (
        <div className="training-video-player-overlay" onClick={handleClose}>
            <div 
                className="training-video-player-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="training-video-player-header">
                    <h2>{video.title}</h2>
                    <button 
                        className="training-video-player-close"
                        onClick={handleClose}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                <div className="training-video-player-content">
                    <video
                        ref={videoRef}
                        src={video.video_url}
                        controls
                        controlsList="nodownload"
                        onTimeUpdate={handleTimeUpdate}
                        onLoadedMetadata={handleLoadedMetadata}
                        onPlay={handlePlay}
                        onPause={handlePause}
                        onEnded={handleEnded}
                        className="training-video-player-video"
                    >
                        Your browser does not support the video tag.
                    </video>
                    
                    {/* Next Video Overlay with Countdown */}
                    {showNextVideo && nextVideo && (
                        <div className="next-video-overlay">
                            <div className="next-video-container">
                                <h3 className="next-video-title">Up Next</h3>
                                
                                {/* Circular Progress Countdown */}
                                <div className="countdown-circle-container">
                                    <svg className="countdown-circle" viewBox="0 0 120 120">
                                        {/* Background circle */}
                                        <circle
                                            className="countdown-circle-bg"
                                            cx="60"
                                            cy="60"
                                            r="54"
                                        />
                                        {/* Progress circle */}
                                        <circle
                                            className="countdown-circle-progress"
                                            cx="60"
                                            cy="60"
                                            r="54"
                                            style={{
                                                strokeDasharray: `${(countdown / 5) * 339.292} 339.292`
                                            }}
                                        />
                                    </svg>
                                    <div className="countdown-number">{countdown}</div>
                                </div>
                                
                                <div className="next-video-info">
                                    <p className="next-video-label">Next video:</p>
                                    <h4 className="next-video-name">{nextVideo.title}</h4>
                                </div>
                                
                                <div className="next-video-actions">
                                    <button 
                                        className="next-video-btn next-video-btn-play"
                                        onClick={playNextNow}
                                    >
                                        <FontAwesomeIcon icon={faPlayCircle} />
                                        Play Now
                                    </button>
                                    <button 
                                        className="next-video-btn next-video-btn-cancel"
                                        onClick={cancelNextVideo}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Rewatch Prompt Overlay */}
                    {showRewatchPrompt && nextVideo && (
                        <div className="next-video-overlay">
                            <div className="next-video-container">
                                <div className="rewatch-icon-container">
                                    <FontAwesomeIcon icon={faCheckCircle} className="rewatch-check-icon" />
                                </div>
                                
                                <h3 className="next-video-title">Already Watched</h3>
                                
                                <div className="next-video-info">
                                    <p className="rewatch-message">
                                        You've already completed this video. Would you like to watch it again or continue to the next video?
                                    </p>
                                </div>
                                
                                <div className="next-video-actions">
                                    <button 
                                        className="next-video-btn next-video-btn-play"
                                        onClick={handleRewatch}
                                    >
                                        <FontAwesomeIcon icon={faPlayCircle} />
                                        Rewatch
                                    </button>
                                    <button 
                                        className="next-video-btn next-video-btn-secondary"
                                        onClick={handleSkipToNext}
                                    >
                                        Play Next
                                    </button>
                                </div>
                                
                                <div className="rewatch-next-video-preview">
                                    <p className="next-video-label">Up next: {nextVideo.title}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                <div className="training-video-player-footer">
                    <p className="training-video-player-description">
                        {video.description}
                    </p>
                    
                    {progress?.is_completed && (
                        <div className="training-video-player-completed">
                            <FontAwesomeIcon icon={faCheckCircle} />
                            <span>Completed</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TrainingVideoPlayer;

